"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import toast from "react-hot-toast";
import { isSeaaiiHost } from "@/lib/brand";
import { applySeaaiiRebrand } from "@/lib/seaaiiRebrand";
import {
  Send,
  CheckCircle2,
  Mail,
  Phone,
  MessageCircle,
  Loader2,
} from "lucide-react";
import { BrandMark } from "@/components/page/auth-shell";

const SUBJECTS = [
  "สนใจแผน Enterprise",
  "ปรึกษาเรื่องการเชื่อมต่อ Facebook / LINE",
  "สอบถามการชำระเงิน / แพ็กเกจ",
  "แจ้งปัญหาการใช้งาน",
  "อื่นๆ",
];

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Host is client-only → resolve the header logo in an effect (brief
  // FlowCloser flash on first paint, same trade-off as the text rebrand).
  const [isSeaaii, setIsSeaaii] = useState(false);
  useEffect(() => {
    setIsSeaaii(isSeaaiiHost(window.location.host));
  }, []);

  // Seaaii host → rebrand the FlowCloser/Formac contact copy + mailto in place.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSeaaiiHost(window.location.host) && rootRef.current) {
      applySeaaiiRebrand(rootRef.current);
    }
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      phone: formData.get("phone"),
      subject: formData.get("subject"),
      message: formData.get("message"),
    };

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setSubmitted(true);
        toast.success("ส่งข้อความเรียบร้อยแล้ว");
      } else {
        toast.error(json.error || "เกิดข้อผิดพลาด กรุณาลองใหม่");
      }
    } catch {
      toast.error("เกิดข้อผิดพลาด กรุณาลองใหม่");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className="min-h-screen"
      style={{
        background: "var(--bg-soft)",
        color: "var(--ink)",
        fontFamily: "'Noto Sans Thai', system-ui, sans-serif",
      }}
    >
      {/* Navy header band */}
      <header
        className="relative overflow-hidden"
        style={{
          color: "#fff",
          background:
            "radial-gradient(900px 400px at 80% -10%, rgba(96,165,250,.45), transparent 60%)," +
            "linear-gradient(135deg, var(--blue-950) 0%, var(--blue-900) 50%, var(--blue-700) 100%)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.18,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%,#000 30%,transparent 80%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 40%,#000 30%,transparent 80%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 pt-10 pb-14">
          <Link href="/" className="inline-flex items-center gap-3 mb-8">
            {isSeaaii ? (
              // White-wordmark Seaaii logo — legible on the navy header.
              <Image
                src="/seaaii/assets/img/logo-footer.png"
                alt="Seaaii"
                width={276}
                height={100}
                priority
                className="h-9 w-auto"
                style={{ objectFit: "contain" }}
              />
            ) : (
              <>
                <BrandMark size={28} />
                <span className="font-bold text-[18px] tracking-tight">
                  <span style={{ color: "#fff" }}>Flow</span>
                  <span
                    className="font-semibold"
                    style={{
                      background: "linear-gradient(90deg, #bfdbfe, #fff)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Closer
                  </span>
                </span>
              </>
            )}
          </Link>
          <h1
            className="text-[36px] sm:text-[44px] font-bold tracking-tight leading-tight"
            style={{ textWrap: "balance" }}
          >
            ติดต่อเรา
          </h1>
          <p className="mt-3 text-[15px] max-w-2xl" style={{ color: "#cfdcf5" }}>
            สอบถามแผน Enterprise ปรึกษาเรื่องการเชื่อมต่อ หรือแจ้งปัญหาการใช้งาน
            ทีมของเราจะตอบกลับภายใน 24 ชั่วโมงในวันทำการ
          </p>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 py-14">
        {submitted ? (
          <SuccessCard />
        ) : (
          <div
            className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] rounded-2xl overflow-hidden"
            style={{
              background: "#fff",
              border: "1px solid var(--line)",
              boxShadow: "0 24px 60px -20px rgba(11,20,55,.12), 0 0 0 1px var(--line)",
            }}
          >
            <ContactInfoPanel />
            <ContactForm onSubmit={handleSubmit} loading={loading} />
          </div>
        )}

        <div className="text-center mt-8">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            กลับสู่หน้าหลัก
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Left panel: contact info ───────────────────────────────
function ContactInfoPanel() {
  return (
    <aside
      className="relative overflow-hidden p-10 lg:p-12 flex flex-col justify-between"
      style={{
        color: "#fff",
        background:
          "radial-gradient(700px 400px at 100% -10%, rgba(96,165,250,.45), transparent 60%)," +
          "linear-gradient(135deg, var(--blue-950) 0%, var(--blue-900) 50%, var(--blue-700) 100%)",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.14,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
          maskImage: "radial-gradient(ellipse 70% 60% at 30% 30%,#000 30%,transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 30% 30%,#000 30%,transparent 80%)",
        }}
      />

      <div className="relative">
        <h2 className="text-[24px] font-bold leading-tight tracking-tight mb-3" style={{ textWrap: "balance" }}>
          พูดคุยกับทีม FlowCloser
        </h2>
        <p className="text-[14px] leading-relaxed" style={{ color: "#cfdcf5" }}>
          ทุกคำถามจะถูกส่งตรงถึงทีม support ของเรา
          เลือกช่องทางที่สะดวกที่สุดสำหรับคุณ
        </p>
      </div>

      <div className="relative space-y-4 mt-10">
        <ContactItem
          icon={<Mail size={16} />}
          label="อีเมล"
          value={
            <a
              href="mailto:success@seaaii.com"
              className="hover:underline"
              style={{ color: "#fff" }}
            >
              success@seaaii.com
            </a>
          }
        />
        <ContactItem
          icon={<Phone size={16} />}
          label="โทร"
          value={
            <a
              href="tel:0955989191"
              className="hover:underline underline-offset-2"
            >
              095-598-9191, 099-745-2266 (จันทร์ – เสาร์ 9.00 – 17.00)
            </a>
          }
        />
        <ContactItem
          icon={<MessageCircle size={16} />}
          label="LINE"
          value={
            <a
              href="https://line.me/R/ti/p/@seaaii"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline underline-offset-2"
            >
              @seaaii
            </a>
          }
        />
      </div>

      <div
        className="relative mt-10 pt-6 border-t text-[11px] leading-relaxed"
        style={{ borderColor: "rgba(255,255,255,.12)", color: "#a9bbe0" }}
      >
        FlowCloser · a product of Super Firm Co., Ltd.
        <br />
        281/95 ถ.วิภาวดีรังสิต แขวงสนามบิน เขตดอนเมือง กรุงเทพฯ 10210
      </div>
    </aside>
  );
}

function ContactItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)" }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: "#a9bbe0" }}>
          {label}
        </div>
        <div className="text-[14px] mt-0.5 break-all" style={{ color: "#fff" }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ─── Right panel: form ──────────────────────────────────────
function ContactForm({
  onSubmit,
  loading,
}: {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  loading: boolean;
}) {
  const inputStyle: React.CSSProperties = {
    border: "1px solid var(--line)",
    background: "#fff",
    color: "var(--ink)",
  };

  return (
    <form onSubmit={onSubmit} className="p-8 lg:p-10 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="ชื่อ-นามสกุล" required>
          <input
            name="name"
            required
            autoComplete="name"
            placeholder="สมชาย ใจดี"
            className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
            style={inputStyle}
          />
        </Field>
        <Field label="เบอร์โทรศัพท์" required>
          <input
            name="phone"
            required
            autoComplete="tel"
            placeholder="08x-xxx-xxxx"
            className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="อีเมล" required>
        <input
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="your@email.com"
          className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
          style={inputStyle}
        />
      </Field>

      <Field label="หัวข้อที่ต้องการปรึกษา" required>
        <select
          name="subject"
          required
          defaultValue={SUBJECTS[0]}
          className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15 appearance-none"
          style={{
            ...inputStyle,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m1 1 5 5 5-5'/%3E%3C/svg%3E\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 16px center",
            paddingRight: 40,
          }}
        >
          {SUBJECTS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>

      <Field label="ข้อความของคุณ" required>
        <textarea
          name="message"
          rows={5}
          required
          maxLength={5000}
          placeholder="บอกเราหน่อยว่าคุณต้องการให้ช่วยอะไร..."
          className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15 resize-none"
          style={inputStyle}
        />
      </Field>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 rounded-xl text-[14px] font-semibold transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
        style={{
          background: "var(--blue-700)",
          color: "#fff",
          boxShadow: "0 1px 0 rgba(255,255,255,.2) inset, 0 8px 22px -8px rgba(37,71,196,.55)",
        }}
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            กำลังส่ง...
          </>
        ) : (
          <>
            ส่งข้อความ
            <Send size={16} />
          </>
        )}
      </button>

      <p className="text-[11px] text-center pt-2" style={{ color: "var(--ink-3)" }}>
        การส่งฟอร์มถือว่ายอมรับ{" "}
        <Link href="/terms" className="hover:underline" style={{ color: "var(--blue-700)" }}>
          เงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัว
        </Link>
      </p>
    </form>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1.5" style={{ color: "var(--ink-2)" }}>
        {label} {required && <span style={{ color: "var(--blue-700)" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Success state ──────────────────────────────────────────
function SuccessCard() {
  return (
    <div
      className="rounded-2xl text-center py-16 px-6"
      style={{
        background: "#fff",
        border: "1px solid var(--line)",
        boxShadow: "0 24px 60px -20px rgba(11,20,55,.12), 0 0 0 1px var(--line)",
      }}
    >
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
        style={{ background: "#ecfdf5", border: "1px solid #a7f3d0" }}
      >
        <CheckCircle2 size={32} style={{ color: "#059669" }} />
      </div>
      <h2 className="text-[22px] font-bold mb-2" style={{ color: "var(--ink)" }}>
        ได้รับข้อความของคุณแล้ว
      </h2>
      <p className="text-[14px] max-w-md mx-auto leading-relaxed" style={{ color: "var(--ink-2)" }}>
        ทีมงาน FlowCloser จะตอบกลับทางอีเมลภายใน 24 ชั่วโมงในวันทำการ
        ขอบคุณที่สนใจบริการของเรา 🙏
      </p>
      <div className="mt-7 flex items-center justify-center gap-3">
        <Link
          href="/"
          className="px-5 py-2.5 rounded-xl text-[13px] font-semibold inline-block"
          style={{
            background: "var(--blue-700)",
            color: "#fff",
            boxShadow: "0 1px 0 rgba(255,255,255,.2) inset, 0 8px 22px -8px rgba(37,71,196,.55)",
          }}
        >
          กลับหน้าหลัก
        </Link>
        <Link
          href="/contact"
          className="px-5 py-2.5 rounded-xl text-[13px] font-semibold inline-block"
          style={{ background: "var(--bg-soft)", color: "var(--ink-2)", border: "1px solid var(--line)" }}
        >
          ส่งข้อความใหม่
        </Link>
      </div>
    </div>
  );
}
